import { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useSearch } from 'react-location';
import TextInput from 'components/core/text-input';
import useApi from 'utils/use-api';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authState, setMe } from 'state/auth';

const ChangePasswordPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch();
  const api = useApi();

  const currentUser = authState().user;
  useEffect(() => {
    document.title = 'Change Password';
    if (!authState().token) {
      navigate({ to: '/signin', replace: true });
    }
  }, [navigate]);

  const changePasswordMutation = useMutation({
    mutationFn: (payload: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
      api.post('/auth/user/change-password', payload).then(res => res.data),
    onSuccess: data => {
      if (data?.user) {
        setMe({ ...(currentUser || {}), ...data.user, mustChangePassword: false } as any);
      }
      toast(JSON.stringify({ type: 'success', title: 'Password updated' }));
      navigate({ to: (search as any)?.redirect ?? '/', replace: true });
    },
  });

  const form = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required('Current password is required'),
      newPassword: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('New password is required')
        .matches(/(?=.*[a-z])/, 'Must contain at least one lowercase letter')
        .matches(/(?=.*[A-Z])/, 'Must contain at least one uppercase letter')
        .matches(/(?=.*\d)/, 'Must contain at least one number')
        .matches(/(?=.*[!@#$%^&*()\-_=+{};:,<.>])/, 'Must contain at least one special character'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Confirm your new password'),
    }),
    onSubmit: values => {
      changePasswordMutation.mutate(values);
    },
  });

  return (
    <div className="max-w-md w-full mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 font-manrope">Set a new password</h1>
      <p className="mt-2 text-sm text-gray-600">You need to update your password before continuing.</p>

      <form onSubmit={form.handleSubmit} className="mt-8 space-y-4">
        <TextInput id="currentPassword" label="Current Password" type="password" required {...form} />

        <div>
          <TextInput id="newPassword" label="New Password" type="password" required {...form} />
          <div className="mt-2 text-xs text-gray-600 space-y-1">
            <p className="font-medium">Password must contain:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>At least 8 characters</li>
              <li>One uppercase letter</li>
              <li>One lowercase letter</li>
              <li>One number</li>
              <li>One special character (!@#$%^&*()-_=+{};:,&lt;.&gt;)</li>
            </ul>
          </div>
        </div>

        <TextInput id="confirmPassword" label="Confirm New Password" type="password" required {...form} />

        <button
          type="submit"
          disabled={changePasswordMutation.isPending}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60"
        >
          {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
};

export default ChangePasswordPage;
